import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import JiraService, { JiraIntegrationStatus } from '@/services/JiraService';
import useAuthStore from '@/store/Auth';
import type { Organization } from '@/types';

interface IntegrationsContextType {
  jiraStatus: JiraIntegrationStatus | null;
  jiraStatusFetched: boolean;
  jiraStatusLoading: boolean;
  hasJiraInstalled: boolean;
  refreshJiraStatus: () => Promise<JiraIntegrationStatus | null>;
  updateJiraStatus: (status: JiraIntegrationStatus | null) => void;
}

const IntegrationsContext = createContext<IntegrationsContextType | null>(null);

const syncAuthJiraStatus = (status: JiraIntegrationStatus | null) => {
  const { organization, updateOrganization } = useAuthStore.getState();

  if (!organization?.id) return;

  updateOrganization({
    ...organization,
    has_set_jira_api_key: Boolean(status?.hasJiraInstalled),
    jira_url: status?.jira_site_url || undefined,
    jira_admin_email: status?.jira_admin_email || undefined,
    jira_auto_sync: status?.auto_sync_jira_updates,
    auto_sync_jira_updates: status?.auto_sync_jira_updates,
  } as Organization);
};

export const IntegrationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const organizationId = useAuthStore((state) => state.organization?.id);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [jiraStatus, setJiraStatus] = useState<JiraIntegrationStatus | null>(
    null
  );
  const [jiraStatusFetched, setJiraStatusFetched] = useState(false);
  const [jiraStatusLoading, setJiraStatusLoading] = useState(false);
  const fetchedForOrgRef = useRef<string | null>(null);

  const updateJiraStatus = useCallback(
    (status: JiraIntegrationStatus | null) => {
      setJiraStatus(status);
      setJiraStatusFetched(true);
      syncAuthJiraStatus(status);
    },
    []
  );

  const refreshJiraStatus =
    useCallback(async (): Promise<JiraIntegrationStatus | null> => {
      if (!organizationId) return null;

      try {
        setJiraStatusLoading(true);
        const status = await JiraService.getIntegrationStatus();
        fetchedForOrgRef.current = organizationId;
        updateJiraStatus(status);
        return status;
      } catch (error) {
        console.error('Failed to fetch Jira integration status', error);
        setJiraStatusFetched(true);
        return null;
      } finally {
        setJiraStatusLoading(false);
      }
    }, [organizationId, updateJiraStatus]);

  useEffect(() => {
    if (!isAuthenticated || !organizationId) {
      fetchedForOrgRef.current = null;
      setJiraStatus(null);
      setJiraStatusFetched(false);
      return;
    }

    if (fetchedForOrgRef.current === organizationId) return;

    refreshJiraStatus();
  }, [isAuthenticated, organizationId, refreshJiraStatus]);

  return (
    <IntegrationsContext.Provider
      value={{
        jiraStatus,
        jiraStatusFetched,
        jiraStatusLoading,
        hasJiraInstalled: Boolean(jiraStatus?.hasJiraInstalled),
        refreshJiraStatus,
        updateJiraStatus,
      }}
    >
      {children}
    </IntegrationsContext.Provider>
  );
};

export const useIntegrationsContext = (): IntegrationsContextType => {
  const context = useContext(IntegrationsContext);
  if (!context) {
    throw new Error(
      'useIntegrationsContext must be used within IntegrationsProvider'
    );
  }
  return context;
};
